// Types
import type { TDateTimeInput } from './types';

/**
 * DateTimeFormatter provides static methods for parsing and formatting
 * datetime strings with timezone awareness.
 *
 * Handles both ISO 8601 format with timezone offset (2025-01-15T01:00:00+00:00)
 * and legacy format without T separator (2025-01-15 01:00:00).
 */
export class DateTimeFormatter {
  /**
   * Parses a datetime input and returns a Date object in local timezone.
   * @param input - Date object, ISO string, or legacy datetime string
   * @returns Date object or null if invalid/empty
   */
  static parse(input: TDateTimeInput): Date | null {
    if (!input) return null;

    if (input instanceof Date) {
      return isNaN(input.getTime()) ? null : input;
    }

    const normalized: string = DateTimeFormatter.normalize(input);
    const date: Date = new Date(normalized);

    return isNaN(date.getTime()) ? null : date;
  }

  /**
   * Formats datetime to DD MMM YYYY (e.g., "22 Sep 2024").
   * Uses Intl.DateTimeFormat for locale-aware formatting.
   */
  static toDayMonthYear(input: TDateTimeInput): string {
    const date: Date | null = DateTimeFormatter.parse(input);

    if (!date) return '';

    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(date);
  }

  /**
   * Formats datetime to DD/MM/YYYY (e.g., "22/09/2024").
   * Matches toLocaleDateString('en-GB') behavior.
   */
  static toShortDate(input: TDateTimeInput): string {
    const date: Date | null = DateTimeFormatter.parse(input);

    if (!date) return '';

    return date.toLocaleDateString('en-GB');
  }

  /**
   * Formats datetime to DD-MM-YYYY (e.g., "22-09-2024").
   * Used for talking points date display.
   */
  static toDashedDate(input: TDateTimeInput): string {
    const date: Date | null = DateTimeFormatter.parse(input);

    if (!date) return '';

    const day: string = String(date.getDate()).padStart(2, '0');
    const month: string = String(date.getMonth() + 1).padStart(2, '0');
    const year: number = date.getFullYear();

    return `${day}-${month}-${year}`;
  }

  /**
   * Formats datetime to DD/MM/YYYY HH:mm (e.g., "22/09/2024 10:41").
   * Used for sync timestamps on external offices.
   */
  static toDateTime(input: TDateTimeInput): string | null {
    const date: Date | null = DateTimeFormatter.parse(input);

    if (!date) return null;

    const day: string = String(date.getDate()).padStart(2, '0');
    const month: string = String(date.getMonth() + 1).padStart(2, '0');
    const year: number = date.getFullYear();
    const hours: string = String(date.getHours()).padStart(2, '0');
    const minutes: string = String(date.getMinutes()).padStart(2, '0');

    return `${day}/${month}/${year} ${hours}:${minutes}`;
  }

  /**
   * Formats datetime to DD/MM/YYYY HH:mm:ss (e.g., "22/09/2024 10:41:45").
   * Used for activity feeds and notes display.
   */
  static toFullDateTime(input: TDateTimeInput): string {
    const date: Date | null = DateTimeFormatter.parse(input);

    if (!date) return '';

    const day: string = String(date.getDate()).padStart(2, '0');
    const month: string = String(date.getMonth() + 1).padStart(2, '0');
    const year: number = date.getFullYear();
    const hours: string = String(date.getHours()).padStart(2, '0');
    const minutes: string = String(date.getMinutes()).padStart(2, '0');
    const seconds: string = String(date.getSeconds()).padStart(2, '0');

    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
  }

  /**
   * Extracts YYYY-MM-DD from a datetime input.
   * Handles timezone offsets correctly by using parsed Date.
   */
  static toIsoDate(input: TDateTimeInput): string {
    const date: Date | null = DateTimeFormatter.parse(input);

    if (!date) return '';

    const year: string = String(date.getFullYear());
    const month: string = String(date.getMonth() + 1).padStart(2, '0');
    const day: string = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  /**
   * Formats datetime to ISO 8601 with timezone offset
   * (e.g., "2026-02-24T15:17:45+00:00").
   */
  static toIso8601(input: TDateTimeInput): string {
    const date: Date | null = DateTimeFormatter.parse(input);

    if (!date) return '';

    const offset: number = -date.getTimezoneOffset();
    const sign: string = offset >= 0 ? '+' : '-';
    const absOffset: number = Math.abs(offset);
    const offHours: string = String(Math.floor(absOffset / 60)).padStart(2, '0');
    const offMins: string = String(absOffset % 60).padStart(2, '0');

    const year: string = String(date.getFullYear());
    const month: string = String(date.getMonth() + 1).padStart(2, '0');
    const day: string = String(date.getDate()).padStart(2, '0');
    const hours: string = String(date.getHours()).padStart(2, '0');
    const minutes: string = String(date.getMinutes()).padStart(2, '0');
    const seconds: string = String(date.getSeconds()).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${sign}${offHours}:${offMins}`;
  }

  /**
   * Formats datetime relative to now:
   *   - Today: "Today, HH:mm"
   *   - 1-29 days ago: "N day(s) ago"
   *   - Otherwise: "MMM D, YYYY"
   */
  static toRelative(input: TDateTimeInput): string {
    const date: Date | null = DateTimeFormatter.parse(input);

    if (!date) return '';

    const RELATIVE_DAYS_THRESHOLD: number = 30;
    const MS_IN_DAY: number = 1000 * 60 * 60 * 24;

    const now: Date = new Date();
    const startOfToday: number = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate()
    ).getTime();
    const startOfDate: number = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    ).getTime();
    const diffDays: number = Math.floor((startOfToday - startOfDate) / MS_IN_DAY);

    if (diffDays === 0) {
      const hours: string = String(date.getHours()).padStart(2, '0');
      const minutes: string = String(date.getMinutes()).padStart(2, '0');

      return `Today, ${hours}:${minutes}`;
    }

    if (diffDays > 0 && diffDays < RELATIVE_DAYS_THRESHOLD) {
      return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
    }

    return new Intl.DateTimeFormat('en-GB', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
  }

  /**
   * Normalizes legacy datetime format to ISO 8601.
   * "2025-01-15 01:00:00" -> "2025-01-15T01:00:00"
   */
  private static normalize(input: string): string {
    if (!input.includes('T') && /^\d{4}-\d{2}-\d{2} /.test(input)) {
      return input.replace(' ', 'T');
    }

    return input;
  }
}
